import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Mail,
  Phone,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Avatar } from '~/components/Avatar/Avatar';
import { SourceBadge, LeadOriginBadges } from '~/components/Badge/Badge';
import {
  StageSelect,
  OwnerSelect,
  EditableStat,
  EditableText,
  ValueEditor,
  NumberEditor,
  DateEditor,
  TextEditor,
  TasksSection,
  NotesAndTimelineSection,
  formatDateOnly,
  formatRelativeDate,
  parseJourney,
} from '~/components/LeadDetailPanel/LeadDetailPanel';
import { LeadConversations } from '~/components/LeadConversations/LeadConversations';
import { useLeadConversations } from '~/lib/conversations-queries';
import { EmailComposer } from '~/components/EmailComposer/EmailComposer';
import { SlaBadge } from '~/components/SlaBadge/SlaBadge';
import { BookingManageCard } from '~/components/BookingManageCard/BookingManageCard';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import { useTenantContext } from '~/lib/tenantContext';
import { slaWithDefaults, type SlaSettings } from '~/lib/sla';
import { currentUser } from '~/lib/pb';
import { can } from '~/lib/permissions';
import { formatPbError } from '~/lib/pb-error';
import {
  useLead,
  useLeadActivities,
  useLeadAttribution,
  useLeadTasks,
  usePipelineStages,
  useTenantUsers,
  useUpdateLead,
  useDeleteLead,
  useDistinctTagsByTenant,
  parseTags,
} from '~/lib/queries';
import { TagEditor } from '~/components/Tags';
import { formatMoney, leadCurrency, tenantCurrency } from '~/lib/capabilities';
import { formExtraFields, formMessage, fieldLabel, messageEchoesFields } from '~/lib/form-inquiry';
import {
  emailTrack,
  emailTrackDetail,
  emailTrackLabel,
  emailTrackStatus,
  emailViaLabel,
  type EmailTrackStatus,
} from '~/lib/email-tracking';
import { SentEmailDialog, ViewEmailButton } from '~/components/SentEmailDialog/SentEmailDialog';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import type { Currency, Lead, Activity, User } from '~/lib/types';
import styles from './_authed.oportunidades_.$leadId.module.css';

export const Route = createFileRoute('/_authed/oportunidades_/$leadId')({
  component: LeadPage,
});

function userDisplayName(u: User | undefined): string {
  if (!u) return '—';
  return u.display_name || u.name || u.email || '—';
}

function contactDisplayName(lead: Lead): string {
  const c = lead.expand?.contact;
  if (!c) return 'Sin contacto';
  return [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || 'Sin nombre';
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
}

function hostPath(raw: string): { label: string; title: string } {
  try {
    const u = new URL(raw);
    const path = u.pathname === '/' ? '' : u.pathname;
    return { label: `${u.host}${path}`, title: raw };
  } catch {
    return { label: raw, title: raw };
  }
}

const TRACK_TIP: Record<EmailTrackStatus, string> = {
  sent: 'Resend todavía no confirmó la entrega.',
  delivered: 'Llegó a la bandeja. Aún no hay apertura.',
  opened: 'Lo abrió. El pixel de Resend avisa cuando se carga el correo.',
  clicked: 'Abrió el correo e hizo clic en un enlace.',
  bounced: 'No se pudo entregar.',
  complained: 'El destinatario lo marcó como spam.',
};

function LeadEmails({
  activities,
  canWrite,
  onCompose,
}: {
  activities: Activity[];
  canWrite: boolean;
  onCompose: () => void;
}) {
  const emails = activities.filter((a) => a.type === 'email_sent');
  const [openId, setOpenId] = useState<string | null>(null);
  const openEmail = emails.find((activity) => activity.id === openId) ?? null;
  return (
    <section className={styles.block}>
      <div className={styles.block__head}>
        {emails.length === 0 ? (
          <p className={styles.emptyMail}>Todavía no le escribes.</p>
        ) : (
          <span />
        )}
        {canWrite && (
          <button type="button" className="btn btn--ghost" onClick={onCompose}>
            <Mail size={14} aria-hidden /> Escribir
          </button>
        )}
      </div>
      {emails.length === 0 ? null : (
        <div className={styles.mailScroll}>
          <table className={styles.mailTable}>
            <thead>
              <tr>
                <th>Cuándo</th>
                <th>Asunto</th>
                <th>Estado</th>
                <th>
                  <span className={styles.srOnly}>Abrir</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {emails.map((activity) => {
                const track = emailTrack(activity.metadata);
                const status = emailTrackStatus(track);
                const via = emailViaLabel(track.via);
                const detail = emailTrackDetail(track);
                return (
                  <tr
                    key={activity.id}
                    className={styles.mailRow}
                    onClick={() => setOpenId(activity.id)}
                  >
                    <td className={styles.mailWhen}>
                      <time dateTime={activity.occurred_at} title={formatDate(activity.occurred_at)}>
                        {formatRelativeDate(activity.occurred_at)}
                      </time>
                    </td>
                    <td className={styles.mailSubject}>
                      <span className={styles.mailSubject__text}>
                        {activity.subject?.trim() || 'Sin asunto'}
                      </span>
                      {via && <span className={styles.mailVia}>{via}</span>}
                    </td>
                    <td>
                      <Tooltip content={TRACK_TIP[status]}>
                        <span className={`${styles.mailStatus} ${styles[`mailStatus--${status}`]}`}>
                          {emailTrackLabel(status)}
                        </span>
                      </Tooltip>
                      {detail && <span className={styles.mailDetail}>{detail}</span>}
                    </td>
                    <td className={styles.mailAction}>
                      <ViewEmailButton onClick={() => setOpenId(activity.id)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <SentEmailDialog
        activity={openEmail}
        open={openEmail != null}
        onOpenChange={(next) => {
          if (!next) setOpenId(null);
        }}
      />
    </section>
  );
}

function LeadPage() {
  const { leadId } = Route.useParams();
  const navigate = useNavigate();
  const { selectedTenant } = useTenantContext();
  const slaThreshold = slaWithDefaults(
    (selectedTenant?.settings as { sla?: SlaSettings } | undefined)?.sla,
  ).first_response_minutes;
  const { data: lead, isLoading, error } = useLead(leadId);
  const { data: stages } = usePipelineStages(lead?.pipeline);
  const { data: users } = useTenantUsers();
  const { data: activities } = useLeadActivities(leadId);
  const { data: tasks } = useLeadTasks(leadId);
  const { data: attribution } = useLeadAttribution(leadId);
  const { data: conversations } = useLeadConversations(leadId);
  const { data: tagSuggestions } = useDistinctTagsByTenant();
  const updateLead = useUpdateLead();
  const deleteLead = useDeleteLead();
  const confirm = useConfirm();
  const canDelete = can(currentUser()?.role, 'deleteRecords');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [workTab, setWorkTab] = useState<'actividad' | 'correos' | 'chat' | 'tareas'>('actividad');
  if (isLoading) {
    return <div className="empty-state">Cargando oportunidad…</div>;
  }
  if (error || !lead) {
    return (
      <div className={styles.notFound}>
        <ShieldAlert size={32} aria-hidden style={{ marginBottom: 12 }} />
        <p>No se encontró la oportunidad o no tienes acceso a ella.</p>
        <Link to="/oportunidades" className={styles.backLink}>
          <ArrowLeft size={14} aria-hidden /> Volver a la lista
        </Link>
      </div>
    );
  }

  const contact = lead.expand?.contact;
  const owner = lead.expand?.assigned_to;
  const name = contactDisplayName(lead);
  const hasGivenName = Boolean(contact?.first_name || contact?.last_name);
  const headline = hasGivenName ? name : contact?.email || lead.title || 'Sin nombre';
  const showOpportunityTitle =
    Boolean(lead.title) && lead.title.trim().toLowerCase() !== headline.trim().toLowerCase();
  const roleLine = [contact?.position, contact?.company].filter(Boolean).join(' · ');
  const openTasks = (tasks ?? []).filter(
    (t) => t.status !== 'done' && t.status !== 'cancelled',
  );
  const doneTasks = (tasks ?? []).filter((t) => t.status === 'done');
  const emailCount = (activities ?? []).filter((a) => a.type === 'email_sent').length;
  const chatCount = conversations?.length ?? 0;
  const activeTab = workTab === 'chat' && chatCount === 0 ? 'actividad' : workTab;
  const inquiryFields = formExtraFields(lead);
  const rawInquiry = formMessage(lead);
  const inquiryMessage = messageEchoesFields(rawInquiry, inquiryFields) ? '' : rawInquiry;

  function patchLead(patch: Partial<Lead>) {
    updateLead.mutate({ leadId: lead!.id, patch });
  }

  async function handleDelete() {
    const title = lead!.title || contactDisplayName(lead!);
    const ok = await confirm({
      title: '¿Eliminar esta oportunidad?',
      description: `Se eliminará «${title}» de forma permanente (tareas y notas asociadas). El contacto no se borra. Esta acción no se puede deshacer.`,
      confirmLabel: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;
    setDeleteError(null);
    try {
      await deleteLead.mutateAsync({ leadId: lead!.id });
      navigate({ to: '/oportunidades' });
    } catch (err) {
      setDeleteError(formatPbError(err, 'No pudimos eliminar la oportunidad.'));
    }
  }

  // El bot crea un record `attribution` por cada lead aunque venga sin
  // utm/referrer/landing — solo con `tenant` y `lead`. Si todos los campos
  // visibles están vacíos, la card no aporta info, así que la ocultamos en
  // vez de renderizar un <dl> sin filas.
  const hasAttributionData =
    !!attribution &&
    !!(
      attribution.utm_source ||
      attribution.utm_medium ||
      attribution.utm_campaign ||
      attribution.utm_term ||
      attribution.utm_content ||
      attribution.gclid ||
      attribution.gbraid ||
      attribution.wbraid ||
      attribution.fbclid ||
      attribution.referrer ||
      attribution.landing_page ||
      attribution.device ||
      attribution.city ||
      attribution.country
    );
  const journeyEntries = parseJourney(attribution?.journey);

  return (
    <div className={styles.page}>
      <header className={styles.workspace}>
        <div className={styles.identityRow}>
        <button
          type="button"
          className={styles.back}
          onClick={() => {
            if (window.history.length > 1) navigate({ to: '..' });
            else navigate({ to: '/oportunidades' });
          }}
          aria-label="Volver"
        >
          <ArrowLeft size={14} aria-hidden />
        </button>

        <div className={styles.who}>
          {contact && <Avatar name={headline} size="lg" />}
          <div className={styles.who__text}>
            <h1 className={styles.headline}>
              {!hasGivenName && !contact?.email ? (
                <EditableText
                  value={lead.title || ''}
                  placeholder="Sin título"
                  onSave={(title) => patchLead({ title })}
                  ariaLabel="Editar título de la oportunidad"
                />
              ) : (
                headline
              )}
            </h1>
            {showOpportunityTitle && (
              <div className={styles.context}>
                <EditableText
                  value={lead.title || ''}
                  placeholder="Sin título"
                  onSave={(title) => patchLead({ title })}
                  ariaLabel="Editar título de la oportunidad"
                />
              </div>
            )}
            {(contact?.email || contact?.phone || roleLine) && (
              <div className={styles.channels}>
                {roleLine && <span className={styles.muted}>{roleLine}</span>}
                {contact?.email && contact.email !== headline && (
                  <a href={`mailto:${contact.email}`} className={styles.channel}>
                    <Mail size={14} aria-hidden /> {contact.email}
                  </a>
                )}
                {contact?.phone && (
                  <a href={`tel:${contact.phone}`} className={styles.channel}>
                    <Phone size={14} aria-hidden /> {contact.phone}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

      </div>

      <div className={styles.statusBar}>
        <div className={styles.statusBar__lead}>
          <StageSelect
            value={lead.stage}
            stages={stages ?? []}
            leadId={lead.id}
            contactId={lead.contact}
            currentLostReason={lead.lost_reason ?? ''}
            onChange={(patch) => patchLead(patch)}
            compact
            prominent
            showQuick={false}
          />
          <SlaBadge createdIso={lead.created} activities={activities ?? []} thresholdMinutes={slaThreshold} />
        </div>
        {contact?.id && contact?.email && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setComposeOpen(true)}
          >
            <Mail size={14} aria-hidden /> Enviar email
          </button>
        )}
      </div>
      </header>

      {((stages ?? []).find((s) => s.id === lead.stage)?.stage_type === 'lost' ||
        !!lead.lost_reason) && (
        <div className={styles.lostReason}>
          <EditableStat
            label="Razón de pérdida"
            display={
              lead.lost_reason ? (
                lead.lost_reason
              ) : (
                <span className={styles.muted}>Sin razón — clic para agregar</span>
              )
            }
            editor={(close) => (
              <TextEditor
                value={lead.lost_reason ?? ''}
                placeholder="Ej: eligió otra opción, sin presupuesto…"
                onSave={(lost_reason) => {
                  patchLead({ lost_reason: lost_reason.slice(0, 500) });
                  close();
                }}
                onCancel={close}
              />
            )}
          />
        </div>
      )}

      <div className={styles.grid}>
        <main className={styles.main}>
          {(inquiryMessage || inquiryFields.length > 0) && (
            <section className={styles.block}>
              <h2 className={styles.block__title}>Consulta</h2>
              {inquiryMessage && <p className={styles.inquiryMessage}>{inquiryMessage}</p>}
              {inquiryFields.length > 0 && (
                <dl className={styles.facts}>
                  {inquiryFields.map(([k, v]) => (
                    <div key={k}>
                      <dt>{fieldLabel(k)}</dt>
                      <dd>{v}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>
          )}

          <div className={styles.work}>
            <div className={`subtabs ${styles.tabs}`} role="tablist" aria-label="Trabajo con esta oportunidad">
              <button
                type="button"
                role="tab"
                id="lead-tab-actividad"
                className={`subtab ${activeTab === 'actividad' ? 'subtab--active' : ''}`}
                aria-selected={activeTab === 'actividad'}
                aria-controls="lead-panel-actividad"
                onClick={() => setWorkTab('actividad')}
              >
                Actividad
              </button>
              <button
                type="button"
                role="tab"
                id="lead-tab-correos"
                className={`subtab ${activeTab === 'correos' ? 'subtab--active' : ''}`}
                aria-selected={activeTab === 'correos'}
                aria-controls="lead-panel-correos"
                onClick={() => setWorkTab('correos')}
              >
                Correos
                <span className={styles.tabCount}>{emailCount}</span>
              </button>
              {chatCount > 0 && (
                <button
                  type="button"
                  role="tab"
                  id="lead-tab-chat"
                  className={`subtab ${activeTab === 'chat' ? 'subtab--active' : ''}`}
                  aria-selected={activeTab === 'chat'}
                  aria-controls="lead-panel-chat"
                  onClick={() => setWorkTab('chat')}
                >
                  Chat
                  <span className={styles.tabCount}>{chatCount}</span>
                </button>
              )}
              <button
                type="button"
                role="tab"
                id="lead-tab-tareas"
                className={`subtab ${activeTab === 'tareas' ? 'subtab--active' : ''}`}
                aria-selected={activeTab === 'tareas'}
                aria-controls="lead-panel-tareas"
                onClick={() => setWorkTab('tareas')}
              >
                Tareas
                <span className={styles.tabCount}>{openTasks.length}</span>
              </button>
            </div>

            {activeTab === 'actividad' && (
              <div role="tabpanel" id="lead-panel-actividad" aria-labelledby="lead-tab-actividad" className={styles.panel}>
                <BookingManageCard leadId={lead.id} />
                <NotesAndTimelineSection lead={lead} activities={activities ?? []} bare />
              </div>
            )}
            {activeTab === 'correos' && (
              <div role="tabpanel" id="lead-panel-correos" aria-labelledby="lead-tab-correos" className={styles.panel}>
                <LeadEmails
                  activities={activities ?? []}
                  canWrite={Boolean(contact?.id && contact?.email)}
                  onCompose={() => setComposeOpen(true)}
                />
              </div>
            )}
            {activeTab === 'chat' && (
              <div role="tabpanel" id="lead-panel-chat" aria-labelledby="lead-tab-chat" className={styles.panel}>
                <LeadConversations leadId={lead.id} bare />
              </div>
            )}
            {activeTab === 'tareas' && (
              <div role="tabpanel" id="lead-panel-tareas" aria-labelledby="lead-tab-tareas" className={styles.panel}>
                <TasksSection
                  lead={lead}
                  tasks={openTasks}
                  users={users ?? []}
                  title=""
                  showAddButton
                  bare
                  emptyLabel="Sin tareas."
                />
                {doneTasks.length > 0 && (
                  <TasksSection
                    lead={lead}
                    tasks={doneTasks}
                    users={users ?? []}
                    title="Completadas"
                    showAddButton={false}
                    bare
                  />
                )}
              </div>
            )}
          </div>

          <footer className={styles.footer}>
            <div className={styles.footer__meta}>
              <span className={styles.footer__item}>
                Creada <strong>{formatDate(lead.created)}</strong>
              </span>
              <span className={styles.footer__item}>
                Última actualización <strong>{formatDate(lead.updated)}</strong>
              </span>
            </div>
            {canDelete && (
              <div className={styles.footer__actions}>
                {deleteError && (
                  <span className={styles.deleteError} role="alert">{deleteError}</span>
                )}
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={handleDelete}
                  disabled={deleteLead.isPending}
                >
                  <Trash2 size={12} aria-hidden />
                  {deleteLead.isPending ? 'Eliminando…' : 'Eliminar oportunidad'}
                </button>
              </div>
            )}
          </footer>
        </main>

        <aside className={styles.rail}>
          <section className={styles.rail__group} aria-label="Datos de la oportunidad">
            <h2 className={styles.block__title}>Oportunidad</h2>
            <div className={styles.prop}>
              <span className={styles.prop__label}>Asignado</span>
              <OwnerSelect
                value={lead.assigned_to ?? null}
                users={users ?? []}
                onChange={(userId) =>
                  patchLead({ assigned_to: userId || undefined } as Partial<Lead>)
                }
                ownerName={userDisplayName(owner)}
              />
            </div>
            <EditableStat
              layout="row"
              label="Valor"
              display={
                lead.value
                  ? formatMoney(lead.value, leadCurrency(lead, selectedTenant))
                  : <span className={styles.placeholder}>Agregar</span>
              }
              editor={(close) => (
                <ValueEditor
                  value={lead.value}
                  currency={tenantCurrency(selectedTenant) as Currency}
                  onSave={(next) => {
                    patchLead(next);
                    close();
                  }}
                  onCancel={close}
                />
              )}
            />
            <EditableStat
              layout="row"
              label="Probabilidad"
              display={
                lead.probability
                  ? `${lead.probability}%`
                  : <span className={styles.placeholder}>Agregar</span>
              }
              editor={(close) => (
                <NumberEditor
                  value={lead.probability}
                  suffix="%"
                  min={0}
                  max={100}
                  onSave={(probability) => {
                    patchLead({ probability });
                    close();
                  }}
                  onCancel={close}
                />
              )}
            />
            <EditableStat
              layout="row"
              label="Cierre"
              display={
                lead.expected_close
                  ? formatDateOnly(lead.expected_close)
                  : <span className={styles.placeholder}>Agregar</span>
              }
              editor={(close) => (
                <DateEditor
                  value={lead.expected_close}
                  onSave={(expected_close) => {
                    patchLead({ expected_close });
                    close();
                  }}
                  onCancel={close}
                />
              )}
            />
            <EditableStat
              layout="row"
              label="Origen"
              display={
                <LeadOriginBadges
                  source={lead.source}
                  tags={parseTags((lead as unknown as { tags?: unknown }).tags)}
                />
              }
              editor={(close) => (
                <TextEditor
                  value={lead.source ?? ''}
                  placeholder="ej: google_ads"
                  onSave={(source) => {
                    patchLead({ source });
                    close();
                  }}
                  onCancel={close}
                />
              )}
            />
          </section>

          <section className={styles.rail__group}>
            <h2 className={styles.block__title}>Tags</h2>
            <TagEditor
              tags={parseTags((lead as unknown as { tags?: unknown }).tags)}
              onChange={(next) => patchLead({ tags: next } as Partial<Lead>)}
              suggestions={(tagSuggestions ?? []).map((s) => s.tag)}
              disabled={updateLead.isPending}
            />
          </section>

          {hasAttributionData && attribution && (
            <section className={styles.rail__group}>
              <h2 className={styles.block__title}>Origen del anuncio</h2>
              <dl className={styles.attribution}>
                {(attribution.gclid || attribution.gbraid || attribution.wbraid) && (
                  <>
                    <dt>Canal</dt>
                    <dd><SourceBadge source="google_ads" /></dd>
                  </>
                )}
                {attribution.fbclid && !attribution.gclid && !attribution.gbraid && (
                  <>
                    <dt>Canal</dt>
                    <dd><SourceBadge source="meta_ads" /></dd>
                  </>
                )}
                {attribution.utm_source && (<><dt>Fuente</dt><dd className={styles.clip} title={attribution.utm_source}>{attribution.utm_source}</dd></>)}
                {attribution.utm_medium && (<><dt>Medio</dt><dd>{attribution.utm_medium}</dd></>)}
                {attribution.utm_campaign && (<><dt>Campaña</dt><dd className={styles.clip} title={attribution.utm_campaign}>{attribution.utm_campaign}</dd></>)}
                {attribution.utm_term && (<><dt>Término</dt><dd className={styles.clip} title={attribution.utm_term}>{attribution.utm_term}</dd></>)}
                {attribution.landing_page && (
                  <>
                    <dt>Página</dt>
                    <dd className={styles.clip} title={hostPath(attribution.landing_page).title}>
                      {hostPath(attribution.landing_page).label}
                    </dd>
                  </>
                )}
                {attribution.referrer && (
                  <>
                    <dt>Referido</dt>
                    <dd className={styles.clip} title={attribution.referrer}>{hostPath(attribution.referrer).label}</dd>
                  </>
                )}
              </dl>
              {(attribution.gclid || attribution.gbraid || attribution.wbraid || attribution.fbclid || attribution.utm_content || attribution.device || attribution.city) && (
                <details className={styles.ids}>
                  <summary>Más detalle</summary>
                  <dl className={styles.attribution}>
                    {attribution.utm_content && (<><dt>Contenido</dt><dd className={styles.clip} title={attribution.utm_content}>{attribution.utm_content}</dd></>)}
                    {attribution.device && (<><dt>Dispositivo</dt><dd className={styles.cap}>{attribution.device}</dd></>)}
                    {attribution.city && (
                      <>
                        <dt>Ubicación</dt>
                        <dd>
                          {attribution.city}
                          {attribution.country ? `, ${attribution.country}` : ''}
                        </dd>
                      </>
                    )}
                    {attribution.gclid && (<><dt>gclid</dt><dd className={styles.mono}>{attribution.gclid}</dd></>)}
                    {attribution.gbraid && (<><dt>gbraid</dt><dd className={styles.mono}>{attribution.gbraid}</dd></>)}
                    {attribution.wbraid && (<><dt>wbraid</dt><dd className={styles.mono}>{attribution.wbraid}</dd></>)}
                    {attribution.fbclid && (<><dt>fbclid</dt><dd className={styles.mono}>{attribution.fbclid}</dd></>)}
                  </dl>
                </details>
              )}
            </section>
          )}

          {journeyEntries.length > 0 && (
            <section className={styles.rail__group}>
              <h2 className={styles.block__title}>Recorrido</h2>
              <ol className={styles.journey}>
                {journeyEntries.map((e, i) => (
                  <li key={`${e.path}-${i}`} className={styles.journey__item}>
                    <span className={styles.journey__path}>{e.path}</span>
                    {e.ts > 0 && (
                      <time className={styles.journey__when} dateTime={new Date(e.ts).toISOString()}>
                        {formatRelativeDate(new Date(e.ts).toISOString())}
                      </time>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          )}
        </aside>
      </div>

      {contact?.id && contact?.email && (
        <Dialog.Root open={composeOpen} onOpenChange={setComposeOpen}>
          <Dialog.Portal>
            <Dialog.Overlay className={styles.dialogOverlay} />
            <Dialog.Content className={styles.dialogContent} aria-describedby={undefined}>
              <Dialog.Title className={styles.srOnly}>Enviar email</Dialog.Title>
              <EmailComposer
                tenantId={lead.tenant}
                contactId={contact.id}
                contactEmail={contact.email}
                leadId={lead.id}
                hasLead
                previewVars={{
                  'contact.first_name': contact.first_name ?? '',
                  'contact.last_name': contact.last_name ?? '',
                  'contact.email': contact.email ?? '',
                  'contact.company': contact.company ?? '',
                  'contact.phone': contact.phone ?? '',
                  'lead.title': lead.title ?? '',
                  'lead.value': lead.value != null ? String(lead.value) : '',
                  'lead.source': lead.source ?? '',
                }}
              />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
}
